import React, { useEffect, useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import axiosInstance from "../utils/axios";
import toast from "react-hot-toast";
import TinyMCEEditor from "../components/QuillEditor";

const AgentInvestmentsTab = () => {
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [editorModalVisible, setEditorModalVisible] = useState(false);
  const [editingInvestment, setEditingInvestment] = useState(null);
  const [newName, setNewName] = useState("");
  const [editorContent, setEditorContent] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchInvestments = () => {
    setLoading(true);
    axiosInstance
      .get("/api/investments")
      .then((res) => setInvestments(Array.isArray(res.data) ? res.data : []))
      .catch((err) => {
        console.error(err);
        toast.error("Failed to load investments");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => fetchInvestments(), []);

  const handleAddInvestment = () => {
    if (!newName.trim()) {
      toast.error("Investment name is required");
      return;
    }
    setSaving(true);
    axiosInstance
      .post("/api/investments", { name: newName.trim() })
      .then(() => {
        toast.success("Investment added");
        setNewName("");
        setAddModalVisible(false);
        fetchInvestments();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.error || "Failed to add investment");
      })
      .finally(() => setSaving(false));
  };

  const openEditor = (investment) => {
    setEditingInvestment(investment);
    setEditorContent(investment.description || "");
    setEditorModalVisible(true);
  };

  const saveDescription = () => {
    if (!editingInvestment) return;
    setSaving(true);
    axiosInstance
      .put(`/api/investments/${editingInvestment.id}`, { description: editorContent })
      .then(() => {
        toast.success("Investment updated");
        setEditorModalVisible(false);
        setEditingInvestment(null);
        fetchInvestments();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.error || "Failed to update investment");
      })
      .finally(() => setSaving(false));
  };

  const handleDelete = (e, investment) => {
    e.stopPropagation();
    if (!window.confirm(`Delete investment "${investment.name}"?`)) return;
    axiosInstance
      .delete(`/api/investments/${investment.id}`)
      .then(() => {
        toast.success("Investment deleted");
        fetchInvestments();
      })
      .catch((err) => {
        toast.error(err?.response?.data?.error || "Failed to delete investment");
      });
  };

  return (
    <div className="p-5">
      <div className="flex justify-between items-center mb-5">
        <h1 className="lg:text-2xl font-semibold text-[#003049]">Investments</h1>
        <Button
          label="Add Investment"
          icon="pi pi-plus"
          className="bg-[#BA1500] text-white btn text-sm focus:outline-none focus:ring-0"
          onClick={() => {
            setNewName("");
            setAddModalVisible(true);
          }}
        />
      </div>

      <DataTable
        value={investments}
        loading={loading}
        emptyMessage="No investments yet. Add one to get started."
        responsiveLayout="scroll"
        className="text-xs sm:text-sm"
        rowClassName="cursor-pointer"
        onRowClick={(e) => openEditor(e.data)}
      >
        <Column
          header="Actions"
          body={(row) => (
            <div className="flex gap-2">
              <Button
                label="Edit"
                className="p-button-text p-button-sm"
                onClick={(e) => {
                  e.stopPropagation();
                  openEditor(row);
                }}
              />
              <Button
                icon="pi pi-trash"
                className="p-button-text p-button-danger p-button-sm"
                onClick={(e) => handleDelete(e, row)}
              />
            </div>
          )}
          style={{ width: 120 }}
        />
        <Column
          field="name"
          header="Investment Name"
          sortable
          body={(row) => (
            <span className="font-medium text-[#003049] hover:underline">
              {row.name}
            </span>
          )}
        />
      </DataTable>

      {/* Add Investment Modal */}
      <Dialog
        header="Add Investment"
        visible={addModalVisible}
        onHide={() => setAddModalVisible(false)}
        className="w-full max-w-md"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              label="Cancel"
              onClick={() => setAddModalVisible(false)}
              className="p-button-text"
            />
            <Button
              label="Add"
              onClick={handleAddInvestment}
              loading={saving}
              disabled={saving || !newName.trim()}
              className="bg-[#BA1500] text-white"
            />
          </div>
        }
      >
        <div className="flex flex-col gap-3 py-2">
          <label className="font-medium">Investment Name</label>
          <InputText
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Enter investment name"
            className="w-full"
          />
        </div>
      </Dialog>

      {/* TinyMCE Editor Modal - Write about investment */}
      <Dialog
        header={editingInvestment ? `Write about: ${editingInvestment.name}` : ""}
        visible={editorModalVisible}
        onHide={() => setEditorModalVisible(false)}
        className="w-full max-w-4xl"
        style={{ width: "90vw" }}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              label="Cancel"
              onClick={() => setEditorModalVisible(false)}
              className="p-button-text"
            />
            <Button
              label="Save"
              onClick={saveDescription}
              loading={saving}
              disabled={saving}
              className="bg-[#BA1500] text-white"
            />
          </div>
        }
      >
        {editorModalVisible && (
          <div className="py-2 min-h-[300px]">
            <TinyMCEEditor
              value={editorContent}
              onChange={setEditorContent}
              height={400}
            />
          </div>
        )}
      </Dialog>
    </div>
  );
};

export default AgentInvestmentsTab;
